import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { resolveLineOAuthPopupCompleteUrl } from "../lib/config";
import { AppError, logError } from "../lib/errors";
import {
  clearLineOAuthContextSnapshot,
  clearLineOAuthTokenSnapshot,
  createLineOAuthPopupSnapshot,
  readLineOAuthContextSnapshot,
  readLineOAuthPopupMessage,
  readLineOAuthTokenSnapshot,
  writeLineOAuthPopupSnapshot,
} from "../lib/lineOAuthPopup";
import { storage } from "../lib/storage";
import { resolveLineLinkSnapshotFromApiStatus, writeLineLinkSnapshot } from "../lib/lineLink";

function tryCloseWindow() {
  try {
    window.close();
  } catch {
    // noop
  }

  try {
    window.open("", "_self");
    window.close();
  } catch {
    // noop
  }
}

function getHeadline(isError: boolean) {
  return isError ? "LINE連携を完了できませんでした" : "LINE連携が完了しました";
}

function getDescription() {
  return "この画面は自動で閉じます。閉じない場合は、元の画面に戻ってください。";
}

function getErrorMessage(errorCode: string | null) {
  switch (errorCode) {
    case "access_denied":
      return "LINEログインがキャンセルされました。";
    case "not_linked":
      return "LINE公式アカウントの友だち追加まで完了してから、もう一度お試しください。";
    case "not_friend":
      return "WizMate公式LINEアカウントを友だち追加、またはブロックを解除してから連携してください。";
    default:
      return "LINE連携に失敗しました。";
  }
}

export default function LineOAuthPopupComplete() {
  const navigate = useNavigate();
  const initialPayload = useMemo(() => readLineOAuthPopupMessage(window.location.search), []);
  const [errorCode, setErrorCode] = useState<string | null>(initialPayload.errorCode);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const finalize = (lineConnect: "success" | "error", nextErrorCode: string | null) => {
      if (cancelled) return;

      const snapshot = createLineOAuthPopupSnapshot({
        type: initialPayload.type,
        lineConnect,
        errorCode: nextErrorCode,
        friendshipStatusChanged: initialPayload.friendshipStatusChanged,
      });

      writeLineOAuthPopupSnapshot(snapshot);

      if (window.opener) {
        try {
          window.opener.postMessage(snapshot, window.location.origin);
        } catch {
          // noop
        }

        tryCloseWindow();
        window.setTimeout(() => tryCloseWindow(), 300);
        window.setTimeout(() => tryCloseWindow(), 1000);
        return;
      }

      const context = readLineOAuthContextSnapshot();
      clearLineOAuthContextSnapshot();
      if (context) {
        navigate(context.returnTo, {
          replace: true,
          state: context.returnState,
        });
        return;
      }

      navigate("/home", { replace: true });
    };

    const run = async () => {
      if (initialPayload.lineConnect === "error") {
        setCompleted(true);
        finalize("error", initialPayload.errorCode);
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");

      if (!code || !state) {
        setErrorCode("missing_code");
        setCompleted(true);
        finalize("error", "missing_code");
        return;
      }

      try {
        const savedToken = readLineOAuthTokenSnapshot();
        if (savedToken) {
          clearLineOAuthTokenSnapshot();
          storage.setAuth({ accessToken: savedToken });
        }

        const response = await api.completeLineOAuth({
          code,
          state,
          redirectUri: resolveLineOAuthPopupCompleteUrl(),
        });
        const snapshot = resolveLineLinkSnapshotFromApiStatus(response);
        writeLineLinkSnapshot(snapshot);

        if (snapshot.status !== "linked") {
          setErrorCode("not_linked");
          setCompleted(true);
          finalize("error", "not_linked");
          return;
        }

        setCompleted(true);
        finalize("success", null);
      } catch (error) {
        logError(error);
        const code = error instanceof AppError && error.code === "FORBIDDEN" ? "not_friend" : "callback_failed";
        setErrorCode(code);
        setCompleted(true);
        finalize("error", code);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [initialPayload, navigate]);

  const isError = Boolean(errorCode);

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-6 py-10 text-[#4A3E39]">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md items-center justify-center">
        <div className="w-full rounded-[2rem] border border-white/80 bg-white/90 p-6 text-center shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm">
          <p className="text-[12px] font-black uppercase tracking-[0.18em] text-[#5F8B57]">Complete</p>
          <h1 className="mt-2 text-2xl font-black text-[#7C5A4E]">{getHeadline(isError)}</h1>
          <p className="mt-3 text-sm leading-6 text-[#6B5851]">
            {completed ? getDescription() : "LINE連携を確認しています..."}
          </p>
          {isError ? (
            <p className="mt-3 text-xs font-bold text-[#A1493E]">{getErrorMessage(errorCode)}</p>
          ) : null}
          <button
            type="button"
            onClick={tryCloseWindow}
            className="mt-5 inline-flex items-center justify-center rounded-full border border-[#E4D5C8] bg-white px-4 py-2 text-xs font-bold text-[#6A4F44] transition hover:bg-[#FFF8F1]"
          >
            閉じる
          </button>
        </div>
      </div>
    </main>
  );
}
